import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
import { intl } from '@ocx-app/lib/translation/TranslationProvider';
import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';

import { LocalOffersLocationContext } from './local-offers-location-context';
import {
  CustomLocationData,
  LocalOffersLocationContextValue,
  GeolocationData,
} from './local-offers-location-context.types';
import { LocalOffersLocationState } from '../local-offers.types';
import { localOffersMessages } from '../local-offers.messages';

export type LocalOffersLocationProviderProps = {
  children: React.ReactNode;
};

export const LocalOffersLocationProvider = ({ children }: LocalOffersLocationProviderProps) => {
  const isLocalOffersEnabled = useConfiguration('localOffers.enabled');

  // If local offers are not enabled, just render children without context
  if (!isLocalOffersEnabled) {
    return children;
  }

  return <LocalOffersLocationProviderCore>{children}</LocalOffersLocationProviderCore>;
};

const LocalOffersLocationProviderCore = ({ children }: LocalOffersLocationProviderProps) => {
  const [locationState, setLocationState] = useState<LocalOffersLocationState>('fetching');
  const [customLocationData, setCustomLocationData] = useState<CustomLocationData | undefined>();
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  // Use refs to avoid re-renders
  const isInitializedRef = useRef(false);

  // Store geolocation data received from location selector (including refresh function)
  const [geolocationData, setGeolocationData] = useState<GeolocationData>({
    coordinates: null,
    permissionStatus: null,
    loading: false,
    refreshState: undefined,
  });

  const snackbar = useSnackbar();
  const { triggerEvent } = useRudderStack();

  // Extract geolocation values for easier access
  const { coordinates, permissionStatus, loading, refreshState } = geolocationData;

  // Handle geolocation response when coordinates come in
  useEffect(() => {
    if (!permissionStatus) {
      return;
    }

    // Initialize location state based on existing geolocation permissions
    if (!isInitializedRef.current) {
      if (permissionStatus === 'granted' && coordinates) {
        // User has granted permission, and we have coordinates - default to nearby
        triggerEvent('local_offers_nearby_selected', { source: 'auto' });
        setLocationState('nearby');
      } else {
        // User has denied permission - default to all locations
        setLocationState('all');
      }
      isInitializedRef.current = true;
      return;
    }

    // If we already initialized, and user is requesting location
    if (!isRequestingLocation) {
      return;
    }
    if (loading) {
      setLocationState('fetching');
      return;
    }
    if (permissionStatus === 'granted') {
      if (coordinates) {
        triggerEvent('local_offers_nearby_selected', { source: 'user_action' });
        setLocationState('nearby');
      }
    } else {
      triggerEvent('local_offers_nearby_selection_failed');
      setLocationState('all');
      snackbar.open(intl.formatMessage(localOffersMessages['localOffers:locationPermissionDenied']), {
        variant: 'error',
      });
    }
    // Reset requesting state after processing
    setIsRequestingLocation(false);
  }, [coordinates, permissionStatus, loading, snackbar, triggerEvent, isRequestingLocation]);

  const setCustomLocation = useCallback(
    (customLocationData: CustomLocationData) => {
      triggerEvent('local_offers_custom_location_selected', {
        placeId: customLocationData.placeId,
        name: customLocationData.name,
      });
      setLocationState('custom');
      setCustomLocationData(customLocationData);
    },
    [triggerEvent],
  );

  const requestCurrentLocation = useCallback(() => {
    setIsRequestingLocation(true);
    if (refreshState) {
      refreshState();
    }
  }, [refreshState]);

  const setNoLocationFilter = useCallback(() => {
    setLocationState('all');
  }, []);

  const updateGeolocationData = useCallback((data: GeolocationData) => {
    setGeolocationData(data);
  }, []);

  const contextValue = useMemo<LocalOffersLocationContextValue>(
    () => ({
      locationState,
      currentCoordinates: coordinates,
      customLocation: customLocationData,
      setCustomLocation,
      requestCurrentLocation,
      setNoLocationFilter,
      updateGeolocationData,
    }),
    [
      locationState,
      coordinates,
      customLocationData,
      setCustomLocation,
      requestCurrentLocation,
      setNoLocationFilter,
      updateGeolocationData,
    ],
  );

  return <LocalOffersLocationContext.Provider value={contextValue}>{children}</LocalOffersLocationContext.Provider>;
};
